import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Plus, Upload, Download, Thermometer, CheckCircle2, AlertTriangle, Wrench, Beaker, PencilLine, Ban, Undo2 } from "lucide-react";
import { EditDialog, RetireDialog } from "@/components/RecordEditor";
import { useFileViewer } from "@/components/FileViewer";
import TemperatureTrend from "@/components/TemperatureTrend";

const Section = ({ title, children, testid }) => (
  <section className="border border-slate-200" data-testid={testid}>
    <header className="border-b border-slate-200 bg-slate-50 px-3 py-2">
      <h2 className="text-base font-semibold text-slate-800 md:text-lg">{title}</h2>
    </header>
    <div className="p-3">{children}</div>
  </section>
);

const EMPTY_TASK = { task: "", frequency: "Monthly", assigned_role: "tech", instructions: "", records_temperature: false, active: true };
const EMPTY_SERVICE = { service_type: "Preventive maintenance", performed_on: "", performed_by: "", description: "", parts_used: "", cost: "", downtime_hours: "", outcome: "" };
const EMPTY_CHANGE = { station: "", reagent_id: "", reagent_name: "", lot_number: "", lot_expiry: "", acknowledge_expired: false, action: "Full change", volume: "", volume_unit: "mL", notes: "" };

export default function EquipmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [item, setItem] = useState(null);
  const [reagents, setReagents] = useState([]);
  const [docs, setDocs] = useState({ file: null, title: "", kind: "Service report" });
  const [taskForm, setTaskForm] = useState({ ...EMPTY_TASK });
  const [serviceForm, setServiceForm] = useState({ ...EMPTY_SERVICE });
  const [changeForm, setChangeForm] = useState({ ...EMPTY_CHANGE });
  const [busy, setBusy] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const { openFile, viewerElement } = useFileViewer();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/equipment/${id}`);
      setItem(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [id]);

  useEffect(() => {
    api.get("/equipment/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/reagents").then((r) => setReagents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!item) return <div className="text-sm text-slate-500" data-testid="equipment-detail-loading">Loading instrument…</div>;
  const canEdit = user?.role !== "viewer";

  const post = async (path, body, label, reset) => {
    setBusy(label);
    try {
      await api.post(path, body);
      toast.success(label);
      reset?.();
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const addTask = (e) => {
    e.preventDefault();
    post(`/equipment/${id}/pm-tasks`, taskForm, "PM task added", () => setTaskForm({ ...EMPTY_TASK }));
  };

  const completeTask = (task) => {
    const temp = task.records_temperature
      ? window.prompt(`Temperature reading (${item.temp_unit || "°C"}) — acceptable ${item.temp_min ?? "—"} to ${item.temp_max ?? "—"}`)
      : null;
    if (task.records_temperature && (temp === null || temp === "")) return;
    post(`/equipment/pm-tasks/${task.id}/complete`,
      { comments: "", temperature: temp === null ? null : Number(temp) }, "Check logged");
  };

  const addService = (e) => {
    e.preventDefault();
    post(`/equipment/${id}/service`, {
      ...serviceForm,
      performed_on: serviceForm.performed_on || null,
      cost: serviceForm.cost === "" ? null : Number(serviceForm.cost),
      downtime_hours: serviceForm.downtime_hours === "" ? null : Number(serviceForm.downtime_hours),
    }, "Service logged", () => setServiceForm({ ...EMPTY_SERVICE }));
  };

  const changeExpired = Boolean(changeForm.lot_expiry
    && new Date(`${changeForm.lot_expiry}T23:59:59`) < new Date());

  const addChange = (e) => {
    e.preventDefault();
    post(`/equipment/${id}/reagent-changes`, {
      ...changeForm,
      reagent_id: changeForm.reagent_id || null,
      lot_expiry: changeForm.lot_expiry || null,
      volume: changeForm.volume === "" ? null : Number(changeForm.volume),
      volume_unit: changeForm.volume_unit,
    }, "Reagent change logged", () => setChangeForm({ ...EMPTY_CHANGE }));
  };

  const setStatus = async (status) => {
    const reason = status === "Out of service" ? window.prompt("Why is it out of service?") || "" : "";
    if (status === "Out of service" && !reason.trim()) return;
    setBusy("status");
    try {
      await api.put(`/equipment/${id}`, { status, status_reason: reason });
      toast.success(`Marked ${status.toLowerCase()}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!docs.file) return toast.error("Choose a file first");
    setBusy("doc");
    const body = new FormData();
    body.append("file", docs.file);
    try {
      await api.post(`/equipment/${id}/documents?title=${encodeURIComponent(docs.title)}&doc_kind=${encodeURIComponent(docs.kind)}`,
        body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setDocs({ file: null, title: "", kind: "Service report" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const openDoc = (docId) => {
    const doc = (item?.documents || []).find((x) => x.id === docId);
    return openFile(`/equipment/documents/${docId}/file`, {
      filename: doc?.filename || "document.pdf",
      title: doc?.title || "Instrument document",
    });
  };

  return (
    <div className="space-y-6" data-testid="equipment-detail-page">
      <Link to="/equipment" className="inline-flex items-center gap-2 text-sm font-semibold text-[#134e4a] hover:underline" data-testid="back-to-equipment">
        <ArrowLeft size={15} /> All instruments
      </Link>

      <div className="flex flex-wrap items-start gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900" data-testid="equipment-name">{item.name}</h1>
            <span className={`px-2 py-0.5 text-[11px] font-bold uppercase ${item.status === "In service" ? "bg-[#134e4a] text-white" : item.status === "Out of service" ? "bg-red-800 text-white" : "bg-slate-300 text-slate-800"}`}
                  data-testid="equipment-status-badge">{item.status}</span>
            {item.pm_overdue > 0 && <span className="bg-red-800 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="equipment-overdue-badge">{item.pm_overdue} PM overdue</span>}
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {item.equipment_type} · {item.manufacturer} {item.model} · {item.department}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Serial {item.serial_number || "—"}{item.asset_tag ? ` · Asset ${item.asset_tag}` : ""}
            {item.location ? ` · ${item.location}` : ""}
            {item.service_start_date ? ` · in service since ${fmtDateTime(item.service_start_date)}` : ""}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {item.vendor ? `${item.vendor}${item.vendor_contact ? ` (${item.vendor_contact})` : ""}` : "No vendor on file"}
            {item.contract_number ? ` · contract ${item.contract_number}` : ""}
            {item.contract_expiry ? ` · expires ${fmtDateTime(item.contract_expiry)}` : ""}
          </p>
          {(item.temp_min !== null && item.temp_min !== undefined) || (item.temp_max !== null && item.temp_max !== undefined) ? (
            <p className="mt-2 inline-flex items-center gap-1 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700" data-testid="equipment-temp-range">
              <Thermometer size={12} /> Acceptable range {item.temp_min ?? "—"} to {item.temp_max ?? "—"} {item.temp_unit}
            </p>
          ) : null}
          {item.sop && (
            <p className="mt-2 text-xs text-slate-600">
              Governed by{" "}
              <Link to={`/documents/${item.sop.id}`} className="font-mono font-semibold text-[#134e4a] hover:underline" data-testid="equipment-sop-link">
                {item.sop.control_number}
              </Link>{" "}
              — {item.sop.title}
            </p>
          )}
          {item.status_reason && item.status !== "In service" && (
            <p className="mt-2 text-sm font-semibold text-red-800" data-testid="equipment-status-reason">{item.status_reason}</p>
          )}
        </div>
        {canEdit && (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="correct-equipment-button"
                    className="h-11 border-slate-300">
              <PencilLine size={15} className="mr-2" /> Correct details
            </Button>
            {item.status === "Retired" ? (
              <Button variant="outline" disabled={busy === "status"} onClick={() => setStatus("In service")}
                      data-testid="restore-equipment-button" className="h-11 border-slate-300">
                <Undo2 size={15} className="mr-2" /> Reinstate
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setRetireOpen(true)} data-testid="retire-equipment-button"
                      className="h-11 border-red-300 text-red-800 hover:bg-red-50">
                <Ban size={15} className="mr-2" /> Retire instrument
              </Button>
            )}
            {item.status === "Out of service" && (
              <Button variant="outline" disabled={busy === "status"} onClick={() => setStatus("In service")}
                      data-testid="equipment-in-service-button" className="h-11 border-slate-300">Return to service</Button>
            )}
            {item.status === "In service" && (
              <Button variant="outline" disabled={busy === "status"} onClick={() => setStatus("Out of service")}
                      data-testid="equipment-out-of-service-button" className="h-11 border-red-300 text-red-800 hover:bg-red-50">
                Mark out of service
              </Button>
            )}
          </div>
        )}
      </div>

      <EditDialog
        open={editOpen} onOpenChange={setEditOpen} testid="correct-equipment-dialog"
        title={`Correct ${item.name}`} values={{ ...item, stations_text: (item.stations || []).join("\n") }}
        description="Fix a wrong serial number, asset tag, model or vendor detail. PM and service history stay attached."
        fields={[
          { name: "name", label: "Instrument name", span: 2 },
          { name: "equipment_type", label: "Type", type: "select", options: meta?.equipment_types || [] },
          { name: "department", label: "Department", type: "select", options: meta?.departments || [] },
          { name: "manufacturer", label: "Manufacturer" },
          { name: "model", label: "Model" },
          { name: "serial_number", label: "Serial number" },
          { name: "asset_tag", label: "Asset tag" },
          { name: "location", label: "Location" },
          { name: "service_start_date", label: "In service since", type: "date" },
          { name: "vendor", label: "Service vendor" },
          { name: "vendor_contact", label: "Vendor contact" },
          { name: "contract_number", label: "Contract number" },
          { name: "contract_expiry", label: "Contract expiry", type: "date" },
          { name: "temp_min", label: "Acceptable temp min", type: "number" },
          { name: "temp_max", label: "Acceptable temp max", type: "number" },
          { name: "stations_text", label: "Stations / positions (one per line, or comma separated)",
            type: "textarea", span: 2 },
          { name: "notes", label: "Notes", type: "textarea", span: 2 },
        ]}
        onSave={async (body) => {
          const { stations_text, ...rest } = body;
          const stations = String(stations_text || "")
            .split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
          await api.put(`/equipment/${id}`, { ...rest, stations });
          toast.success("Instrument corrected");
          load();
        }}
      />

      <RetireDialog
        open={retireOpen} onOpenChange={setRetireOpen} testid="retire-equipment-dialog"
        title={`Retire ${item.name}`} confirmLabel="Retire instrument"
        description="Retired instruments drop off the register and PM worklists but keep their full service history for inspectors."
        onConfirm={async (reason) => {
          await api.put(`/equipment/${id}`, { status: "Retired", status_reason: reason });
          toast.success("Instrument retired");
          load();
        }}
      />

      <Tabs defaultValue="pm">
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 py-1">
          <TabsTrigger value="pm" data-testid="tab-pm">PM schedule ({item.pm_tasks.length})</TabsTrigger>
          <TabsTrigger value="service" data-testid="tab-service">Service log ({item.service_logs.length})</TabsTrigger>
          {item.tracks_reagent_changes && (
            <TabsTrigger value="reagents" data-testid="tab-reagent-changes">Reagent changes ({item.reagent_changes.length})</TabsTrigger>
          )}
          <TabsTrigger value="temps" data-testid="tab-temperatures">Temperature trend</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-pm-history">Check history ({item.pm_completions.length})</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-equipment-docs">Documents ({item.documents.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pm" className="mt-4 space-y-4">
          {canEdit && (
            <Section title="Add a scheduled task" testid="pm-task-form">
              <form onSubmit={addTask} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="lg:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Task</Label>
                  <Input value={taskForm.task} onChange={(e) => setTaskForm((f) => ({ ...f, task: e.target.value }))}
                         data-testid="pm-task-input" required placeholder="e.g. Change processor filters" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Frequency</Label>
                  <select value={taskForm.frequency} onChange={(e) => setTaskForm((f) => ({ ...f, frequency: e.target.value }))}
                          data-testid="pm-frequency-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                    {(meta?.frequencies || []).map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Performed by</Label>
                  <select value={taskForm.assigned_role} onChange={(e) => setTaskForm((f) => ({ ...f, assigned_role: e.target.value }))}
                          data-testid="pm-role-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                    {(meta?.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
                    <option value="vendor">vendor</option>
                  </select>
                </div>
                <div className="lg:col-span-3">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Instructions</Label>
                  <Input value={taskForm.instructions} onChange={(e) => setTaskForm((f) => ({ ...f, instructions: e.target.value }))}
                         data-testid="pm-instructions-input" className="mt-1 h-11 border-slate-300" />
                </div>
                <div className="flex items-end gap-3">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={taskForm.records_temperature}
                           onChange={(e) => setTaskForm((f) => ({ ...f, records_temperature: e.target.checked }))}
                           data-testid="pm-records-temp-input" className="h-4 w-4 accent-[#134e4a]" />
                    Records temp
                  </label>
                  <Button type="submit" disabled={busy === "PM task added"} data-testid="add-pm-task-button"
                          className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]"><Plus size={15} /></Button>
                </div>
              </form>
            </Section>
          )}
          <Section title="Scheduled tasks" testid="pm-task-list">
            {item.pm_tasks.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="pm-tasks-empty">No PM tasks scheduled yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {item.pm_tasks.map((t) => (
                  <li key={t.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`pm-task-${t.id}`}>
                    <span className="font-semibold text-slate-900">{t.task}</span>
                    <span className="bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">{t.frequency}</span>
                    <span className="text-xs text-slate-500">by {t.assigned_role}</span>
                    {t.records_temperature && <Thermometer size={13} className="text-slate-500" />}
                    <span className="text-xs text-slate-600">
                      last {t.last_completed_at ? `${fmtDateTime(t.last_completed_at)} (${t.last_completed_by})` : "never"}
                    </span>
                    <span className={`ml-auto text-xs font-semibold ${t.overdue ? "text-red-800" : t.due_soon ? "text-amber-700" : "text-slate-600"}`}
                          data-testid={`pm-task-due-${t.id}`}>
                      {t.overdue ? `${Math.abs(t.days_until_due)}d overdue` : `due in ${t.days_until_due}d`}
                    </span>
                    {canEdit && (
                      <Button size="sm" onClick={() => completeTask(t)} data-testid={`pm-task-complete-${t.id}`}
                              className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">
                        <CheckCircle2 size={14} className="mr-1" /> Log done
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="service" className="mt-4 space-y-4">
          {canEdit && (
            <Section title="Log service, repair or calibration" testid="service-form">
              <form onSubmit={addService} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Type</Label>
                  <select value={serviceForm.service_type} onChange={(e) => setServiceForm((f) => ({ ...f, service_type: e.target.value }))}
                          data-testid="service-type-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                    {(meta?.service_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Date</Label>
                  <Input type="date" value={serviceForm.performed_on} onChange={(e) => setServiceForm((f) => ({ ...f, performed_on: e.target.value }))}
                         data-testid="service-date-input" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Performed by</Label>
                  <Input value={serviceForm.performed_by} onChange={(e) => setServiceForm((f) => ({ ...f, performed_by: e.target.value }))}
                         data-testid="service-by-input" placeholder="Tech or vendor engineer" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Downtime (hours)</Label>
                  <Input type="number" min="0" step="any" value={serviceForm.downtime_hours}
                         onChange={(e) => setServiceForm((f) => ({ ...f, downtime_hours: e.target.value }))}
                         data-testid="service-downtime-input" className="mt-1 h-11 border-slate-300" />
                </div>
                <div className="lg:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">What was done</Label>
                  <Textarea value={serviceForm.description} onChange={(e) => setServiceForm((f) => ({ ...f, description: e.target.value }))}
                            data-testid="service-description-input" required className="mt-1 min-h-[70px] border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Parts used</Label>
                  <Input value={serviceForm.parts_used} onChange={(e) => setServiceForm((f) => ({ ...f, parts_used: e.target.value }))}
                         data-testid="service-parts-input" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Cost</Label>
                  <Input type="number" min="0" step="any" value={serviceForm.cost}
                         onChange={(e) => setServiceForm((f) => ({ ...f, cost: e.target.value }))}
                         data-testid="service-cost-input" className="mt-1 h-11 border-slate-300" />
                </div>
                <div className="lg:col-span-4">
                  <Button type="submit" disabled={busy === "Service logged"} data-testid="add-service-button"
                          className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]"><Wrench size={15} className="mr-2" /> Log service</Button>
                </div>
              </form>
            </Section>
          )}
          <Section title="Service history" testid="service-list">
            {item.service_logs.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="service-empty">No service recorded yet.</p>
            ) : (
              <div className="space-y-2">
                {item.service_logs.map((s) => (
                  <div key={s.id} className="border border-slate-200 p-3 text-sm" data-testid={`service-${s.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900">{s.service_type}</span>
                      <span className="text-xs text-slate-500">{fmtDateTime(s.performed_on)} · {s.performed_by}</span>
                      {s.downtime_hours ? <span className="bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">{s.downtime_hours}h downtime</span> : null}
                      {s.cost ? <span className="num ml-auto text-xs font-semibold text-slate-700">${s.cost}</span> : null}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-slate-700">{s.description}</p>
                    {(s.parts_used || s.outcome) && (
                      <p className="mt-1 text-xs text-slate-600">
                        {s.parts_used ? `Parts: ${s.parts_used}` : ""}{s.parts_used && s.outcome ? " · " : ""}{s.outcome}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {item.tracks_reagent_changes && (
          <TabsContent value="reagents" className="mt-4 space-y-4">
            {canEdit && (
              <Section title="Log a reagent change" testid="reagent-change-form">
                <form onSubmit={addChange} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Station / position</Label>
                    {(item.stations || []).length > 0 ? (
                      <select value={changeForm.station}
                              onChange={(e) => setChangeForm((f) => ({ ...f, station: e.target.value }))}
                              data-testid="change-station-input"
                              className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                        <option value="">Select station…</option>
                        {item.stations.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <>
                        <Input value={changeForm.station} onChange={(e) => setChangeForm((f) => ({ ...f, station: e.target.value }))}
                               data-testid="change-station-input" placeholder="e.g. Station 4 / Xylene 2" className="mt-1 h-11 border-slate-300" />
                        <p className="mt-1 text-[11px] text-slate-500">
                          Set this instrument's stations under Correct instrument to get a dropdown instead.
                        </p>
                      </>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Reagent (ChemTraX)</Label>
                    <select value={changeForm.reagent_id} onChange={(e) => setChangeForm((f) => ({ ...f, reagent_id: e.target.value }))}
                            data-testid="change-reagent-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                      <option value="">Type a name instead</option>
                      {reagents.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                  {!changeForm.reagent_id && (
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-slate-600">Reagent name</Label>
                      <Input value={changeForm.reagent_name} onChange={(e) => setChangeForm((f) => ({ ...f, reagent_name: e.target.value }))}
                             data-testid="change-reagent-name-input" className="mt-1 h-11 border-slate-300" />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Lot number</Label>
                    <Input value={changeForm.lot_number} onChange={(e) => setChangeForm((f) => ({ ...f, lot_number: e.target.value }))}
                           data-testid="change-lot-input" className="mt-1 h-11 border-slate-300" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Lot expiration date</Label>
                    <Input type="date" value={changeForm.lot_expiry}
                           onChange={(e) => setChangeForm((f) => ({ ...f, lot_expiry: e.target.value }))}
                           data-testid="change-lot-expiry-input" className="mt-1 h-11 border-slate-300" />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Expiry of the lot that went on the instrument. Leave blank to use the date already
                      on file for this lot in ChemTraX.
                    </p>
                    {changeExpired && (
                      <div className="mt-2 border-l-2 border-red-700 bg-red-50 p-2"
                           data-testid="change-expired-warning">
                        <p className="text-xs font-bold uppercase tracking-wide text-red-800">
                          This lot expired {fmtDate(changeForm.lot_expiry)}
                        </p>
                        <p className="mt-1 text-xs text-red-900">
                          An expired reagent should not go on the instrument. Pull a current lot if you
                          have one.
                        </p>
                        <label className="mt-2 flex items-start gap-2 text-xs font-semibold text-red-900">
                          <input type="checkbox" checked={changeForm.acknowledge_expired}
                                 data-testid="change-expired-acknowledge"
                                 onChange={(e) => setChangeForm((f) => ({ ...f, acknowledge_expired: e.target.checked }))}
                                 className="mt-0.5 h-4 w-4" />
                          <span>I am loading it anyway and have said why in the notes below.</span>
                        </label>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Action</Label>
                    <select value={changeForm.action} onChange={(e) => setChangeForm((f) => ({ ...f, action: e.target.value }))}
                            data-testid="change-action-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                      {(meta?.reagent_change_actions || []).map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">
                      Volume{changeForm.action === "Full change" ? " (required)" : ""}
                    </Label>
                    <div className="mt-1 flex gap-2">
                      <Input type="number" min="0" step="any" value={changeForm.volume}
                             onChange={(e) => setChangeForm((f) => ({ ...f, volume: e.target.value }))}
                             data-testid="change-volume-input" className="h-11 border-slate-300" />
                      <select value={changeForm.volume_unit}
                              onChange={(e) => setChangeForm((f) => ({ ...f, volume_unit: e.target.value }))}
                              data-testid="change-volume-unit-input"
                              className="h-11 w-20 shrink-0 border border-slate-300 bg-white px-2 text-sm">
                        {(meta?.volume_units || ["mL", "L", "gal"]).map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                      How much went into the station — the consumption record for this lot.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
                    <Input value={changeForm.notes} onChange={(e) => setChangeForm((f) => ({ ...f, notes: e.target.value }))}
                           data-testid="change-notes-input" className="mt-1 h-11 border-slate-300" />
                  </div>
                  <div className="lg:col-span-4">
                    <Button type="submit" disabled={busy === "Reagent change logged"} data-testid="add-reagent-change-button"
                            className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]"><Beaker size={15} className="mr-2" /> Log change</Button>
                  </div>
                </form>
              </Section>
            )}
            <Section title="Reagent change history" testid="reagent-change-list">
              {item.reagent_changes.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="reagent-changes-empty">No reagent changes logged yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr className="border-b border-slate-200">
                        <th className="px-2 py-2">Date</th><th className="px-2 py-2">Station</th><th className="px-2 py-2">Reagent</th>
                        <th className="px-2 py-2">Lot</th><th className="px-2 py-2">Lot expires</th><th className="px-2 py-2">Action</th>
                        <th className="px-2 py-2 text-right">Volume</th><th className="px-2 py-2">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.reagent_changes.map((c) => (
                        <tr key={c.id} className="border-b border-slate-100" data-testid={`reagent-change-${c.id}`}>
                          <td className="px-2 py-2 text-xs text-slate-600">{fmtDateTime(c.changed_on)}</td>
                          <td className="px-2 py-2 text-slate-700">{c.station || "—"}</td>
                          <td className="px-2 py-2 font-medium text-slate-800">{c.reagent_name}</td>
                          <td className="px-2 py-2 font-mono text-xs">{c.lot_number || "—"}</td>
                          <td className="px-2 py-2 text-xs" data-testid={`reagent-change-expiry-${c.id}`}>
                            {c.lot_expiry
                              ? <span className={c.lot_expired ? "font-bold text-red-800" : "text-slate-700"}>
                                  {fmtDate(c.lot_expiry)}
                                  {c.lot_expired && <span className="ml-1 bg-red-800 px-1 text-[10px] font-bold text-white">EXPIRED</span>}
                                </span>
                              : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-2 py-2 text-slate-700">{c.action}</td>
                          <td className="num px-2 py-2 text-right text-slate-700">
                            {c.volume === null || c.volume === undefined ? "—"
                              : `${c.volume}${c.volume_unit ? ` ${c.volume_unit}` : ""}`}
                          </td>
                          <td className="px-2 py-2 text-xs text-slate-600">{c.changed_by}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </TabsContent>
        )}

        <TabsContent value="temps" className="mt-4">
          <Section title="Temperature trend" testid="temperature-trend-section">
            <TemperatureTrend equipmentId={id} equipmentName={item.name} />
          </Section>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Section title="Completed checks" testid="pm-history-list">
            {item.pm_completions.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="pm-history-empty">No checks logged yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {item.pm_completions.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`pm-completion-${c.id}`}>
                    <span className="font-medium text-slate-900">{c.task}</span>
                    <span className="text-xs text-slate-500">{fmtDateTime(c.performed_on)} · {c.performed_by}</span>
                    {c.temperature !== null && c.temperature !== undefined && (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-semibold ${c.temp_out_of_range ? "bg-red-800 text-white" : "bg-slate-100 text-slate-700"}`}
                            data-testid={`pm-temp-${c.id}`}>
                        {c.temp_out_of_range ? <AlertTriangle size={11} /> : <Thermometer size={11} />}
                        {c.temperature}{c.temp_unit}{c.temp_out_of_range ? " out of range" : ""}
                      </span>
                    )}
                    {c.comments && <span className="text-xs text-slate-600">{c.comments}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="docs" className="mt-4 space-y-4">
          {canEdit && (
            <Section title="Upload a service report or calibration certificate" testid="equipment-doc-form">
              <form onSubmit={uploadDoc} className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] flex-1">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">File</Label>
                  <Input type="file" accept=".pdf,.doc,.docx,.txt,.xlsx" onChange={(e) => setDocs((d) => ({ ...d, file: e.target.files?.[0] || null }))}
                         data-testid="equipment-doc-file-input" className="mt-1 h-11 border-slate-300" />
                </div>
                <div className="min-w-[180px]">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Title</Label>
                  <Input value={docs.title} onChange={(e) => setDocs((d) => ({ ...d, title: e.target.value }))}
                         data-testid="equipment-doc-title-input" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Kind</Label>
                  <select value={docs.kind} onChange={(e) => setDocs((d) => ({ ...d, kind: e.target.value }))}
                          data-testid="equipment-doc-kind-input" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
                    <option>Service report</option><option>Calibration certificate</option>
                    <option>Validation</option><option>Manual</option><option>Other</option>
                  </select>
                </div>
                <Button type="submit" disabled={busy === "doc"} data-testid="upload-equipment-doc-button"
                        className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Upload size={15} className="mr-2" />{busy === "doc" ? "Uploading…" : "Upload"}
                </Button>
              </form>
            </Section>
          )}
          <Section title="Documents on file" testid="equipment-doc-list">
            {item.documents.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="equipment-docs-empty">No documents uploaded yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {item.documents.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`equipment-doc-${d.id}`}>
                    <span className="font-medium text-slate-900">{d.title}</span>
                    <span className="bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">{d.doc_kind}</span>
                    <span className="text-xs text-slate-500">{Math.round((d.size || 0) / 1024)} KB · {d.uploaded_by} · {fmtDateTime(d.created_at)}</span>
                    <Button size="sm" variant="outline" onClick={() => openDoc(d.id)}
                            data-testid={`open-equipment-doc-${d.id}`} className="ml-auto h-9 border-slate-300">
                      <Download size={14} className="mr-1" /> Open
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </TabsContent>
      </Tabs>
      {viewerElement}
    </div>
  );
}
